import { Box, Divider, Theme, Typography } from '@mui/material';
import { UserAccount } from '@components/container/UserAccount';
import { useMemo } from 'react';
import { HtmlContent } from '@components/noteMessage/CardNote';
import { format } from 'date-fns-tz';
import { getNameInitials } from '@utils/regex';
import { getDateFormatter, LongDateFormatWithoutSeconds, ShortDateFormat } from '@utils/date';
import {
  IQuoteActivityItem,
  QuoteActivityItemType,
  NewQuoteStatusChangeItem,
  NewNoteItem,
  NewQuoteRatingItem,
  NewDocumentItem,
  activityItemTypeIcon,
  activityItemTypeText,
  ratingText,
  statusText,
  NewUnderwritingActivityItem,
} from '@pages/QuoteDetails/sidebarItems/summary/quoteActivity/QuoteActivityItem.constants';
import { getRatingErrors } from '@pages/QuoteDetails/helpers/getRatingErrors';
import { Button } from '@components/button';
import { quoteRatingStatus } from '@models/quoteRating';
import { DocumentType } from '@models/document';
import { Tooltip } from '@components/tooltip';
import { QuoteType } from '@models/quotes';
import { formatCurrency } from '@utils/number';
import { ViewQuoteRatingSummaryButton } from '@pages/QuoteDetails/sidebarItems/summary/quoteActivity/ViewQuoteRatingSummaryButton';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { getTypographyVariants } from '@theme/styleOverrides';
import { adjustFontSize } from '@theme/styleOverrides/typography';

export const QuoteActivityItem = ({ highlightedItem, onSelectItem, item, isLast, quoteType }: IQuoteActivityItem) => {
  const [{ isInternalUser }, layoutPreferences] = useStore(
    (state) => [state.userAuth, state.layoutPreferences],
    shallow,
  );
  const clockFormat = layoutPreferences.clockFormat;

  const { activityItemType } = item;
  const content = useMemo(() => {
    if (activityItemType === QuoteActivityItemType.QuoteStatusChange) {
      const currentItem = item as NewQuoteStatusChangeItem;
      return (
        <Typography
          variant='bodySmall'
          sx={{
            color: 'text.main',
          }}
        >
          {statusText} {currentItem.newQuoteStatus.label}
        </Typography>
      );
    }

    if (activityItemType === QuoteActivityItemType.QuoteRating) {
      const currentItem = item as NewQuoteRatingItem;
      const { status } = currentItem;
      const ratingErrors = getRatingErrors(currentItem);
      return (
        <Typography
          variant='bodySmall'
          sx={{
            color: 'text.main',
          }}
        >
          {status === quoteRatingStatus.Succeeded &&
          quoteType === QuoteType.Endorsement &&
          currentItem.endorsementDetails // need this check to avoid null/old data from breaking
            ? `Endorsement quote rating succeeded with estimated annual premium of ${formatCurrency(currentItem?.totalPremium)}${currentItem?.termTaxFeeSurcharge ? ` (with taxes, fees, and surcharges of ${formatCurrency(currentItem?.termTaxFeeSurcharge)})` : ''}, estimated new policy premium of ${formatCurrency(currentItem.endorsementDetails?.newPolicyCost)}, and estimated premium change of ${formatCurrency(currentItem.endorsementDetails?.endorsementPremiumChange)}`
            : status === quoteRatingStatus.Failed
              ? ratingText[quoteRatingStatus.Failed](ratingErrors[0])
              : ratingText[quoteRatingStatus.Succeeded](currentItem?.termPremium, currentItem?.termTaxFeeSurcharge)}
        </Typography>
      );
    }

    if (activityItemType === QuoteActivityItemType.Document) {
      const currentItem = item as NewDocumentItem;
      const { documentType } = currentItem;

      return (
        <>
          <Typography variant='bodySmall' color='font.main'>
            Quote {documentType.id === DocumentType.QuoteProposal.id ? 'Proposal' : 'Application'} Generated:
          </Typography>
          <Button variant='text' component='a' target='_blank' sx={{ padding: 0 }} href={currentItem.file?.downloadUrl}>
            {currentItem.file?.fileName}
          </Button>
        </>
      );
    }

    if (activityItemType === QuoteActivityItemType.UnderwritingActivity) {
      const currentItem = item as NewUnderwritingActivityItem;
      const isUnresolved = !currentItem.resolvedAt;

      const activityLabel = currentItem?.underwritingActivityType?.label || 'N/A';
      const activityResolvedReason = currentItem?.resolutionReason?.label || 'N/A';
      const createdAt = currentItem.createdAt
        ? format(new Date(currentItem.createdAt), getDateFormatter(clockFormat, ShortDateFormat))
        : 'N/A';
      const resolvedAt = currentItem.resolvedAt
        ? format(new Date(currentItem.resolvedAt), getDateFormatter(clockFormat, ShortDateFormat))
        : 'N/A';
      return (
        <>
          <Typography variant='bodySmall' color='font.main' mt={2} display='block' sx={{ whiteSpace: 'pre-line' }}>
            {isUnresolved
              ? `Underwriting Activity "${activityLabel}" was created.`
              : `Underwriting Activity "${activityLabel}" was resolved with reason "${activityResolvedReason}"\nActivity Time: ${createdAt} - ${resolvedAt}`}
          </Typography>{' '}
          {currentItem.description && (
            <Typography variant='bodySmall' color='font.main' mt={2} display='block'>
              Description: {currentItem.description}
            </Typography>
          )}
          {currentItem.note && (
            <Typography variant='bodySmall' color='font.main' mt={2} display='block'>
              Note: {currentItem.note}
            </Typography>
          )}
        </>
      );
    }

    const currentItem = item as NewNoteItem;
    if (activityItemType === QuoteActivityItemType.Note) {
      return <HtmlContent body={currentItem?.body ?? ''} />;
    }
    return <HtmlContent body={currentItem?.body ?? ''} />;
  }, [activityItemType, clockFormat, item, quoteType]);

  const user: { firstName: string; lastName: string } = useMemo(() => {
    if (activityItemType === QuoteActivityItemType.QuoteStatusChange) {
      const currentItem = item as NewQuoteStatusChangeItem;
      return {
        firstName: currentItem.changedByUser?.firstName ?? '',
        lastName: currentItem.changedByUser?.lastName ?? '',
      };
    }
    if (activityItemType === QuoteActivityItemType.QuoteRating) {
      const currentItem = item as NewQuoteRatingItem;
      return {
        firstName: currentItem.ratedByUser?.firstName ?? '',
        lastName: currentItem.ratedByUser?.lastName ?? '',
      };
    }

    if (activityItemType === QuoteActivityItemType.Document) {
      const { documentGeneration } = item as NewDocumentItem;

      return {
        firstName: documentGeneration?.triggeredByUser?.firstName ?? '',
        lastName: documentGeneration?.triggeredByUser?.lastName ?? '',
      };
    }
    const currentItem = item as NewNoteItem;
    return {
      firstName: currentItem.createdByUser?.firstName ?? '',
      lastName: currentItem.createdByUser?.lastName ?? '',
    };
  }, [item, activityItemType]);

  const [deletedByInformation, replacedByNoteId] = useMemo(() => {
    if (activityItemType === QuoteActivityItemType.QuoteStatusChange) {
      return [null, null];
    }
    if (activityItemType === QuoteActivityItemType.QuoteRating) {
      return [null, null];
    }

    if (activityItemType === QuoteActivityItemType.Document) {
      return [null, null];
    }
    const currentItem = item as NewNoteItem;
    if (!currentItem.deletedAt) {
      return [null, currentItem.replacesNoteId];
    }

    return [
      {
        deletedByUser: currentItem.deletedByUser
          ? {
              firstName: currentItem.deletedByUser?.firstName ?? '',
              lastName: currentItem.deletedByUser?.lastName ?? '',
            }
          : undefined,
        deletedAt: currentItem.deletedAt
          ? format(new Date(currentItem.deletedAt), getDateFormatter(clockFormat, LongDateFormatWithoutSeconds))
          : undefined,
      },
      currentItem.replacesNoteId,
    ];
  }, [item, activityItemType, clockFormat]);

  const uniqueDomId = `quote-item-${item.id}-anchor`;
  const highlighted = highlightedItem === uniqueDomId;

  const showQuoteRatingSummary =
    isInternalUser &&
    item.activityItemType === QuoteActivityItemType.QuoteRating &&
    (item as NewQuoteRatingItem)?.status === quoteRatingStatus.Succeeded;

  return (
    <Box
      id={uniqueDomId}
      sx={{
        backgroundColor: highlighted ? 'background.main' : 'transparent',
        padding: '16px',
        width: '100%',
        marginRight: '5px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '16px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            height: '16px',
          }}
        >
          {activityItemTypeIcon[activityItemType as QuoteActivityItemType]}
          {item.activityItemType === QuoteActivityItemType.UnderwritingActivity ? (
            <Typography variant='caption'>
              {(item as NewUnderwritingActivityItem).resolvedAt ? 'Activity Resolved' : 'Activity Created'}
            </Typography>
          ) : (
            <Typography variant='caption'>{activityItemTypeText[activityItemType as QuoteActivityItemType]}</Typography>
          )}
        </Box>

        <Typography variant='caption'>
          {item.createdAt
            ? format(new Date(item.createdAt), getDateFormatter(clockFormat, LongDateFormatWithoutSeconds))
            : ''}
        </Typography>
      </Box>

      <Box
        sx={{
          marginLeft: '18px',
        }}
      >
        {deletedByInformation && (
          <Box sx={{ mb: replacedByNoteId ? 0 : 1, padding: 0 }}>
            <Tooltip
              title={`Deleted ${deletedByInformation.deletedAt} by ${deletedByInformation.deletedByUser?.firstName} ${deletedByInformation.deletedByUser?.lastName}`}
            >
              <Typography variant='caption'>Deleted {deletedByInformation.deletedAt}</Typography>
            </Tooltip>
          </Box>
        )}

        {replacedByNoteId && (
          <Box sx={{ mb: 1, padding: 0 }}>
            <Button
              variant='text'
              sx={{
                padding: 0,
                fontSize: (theme: Theme) => getTypographyVariants(theme.palette.fontSize).caption.fontSize,
              }}
              onClick={() => onSelectItem(`quote-item-${replacedByNoteId}-anchor`)}
            >
              Go to Replaced Note
            </Button>
          </Box>
        )}
        {content}

        {showQuoteRatingSummary && (
          <Box sx={{ display: 'block' }}>
            <ViewQuoteRatingSummaryButton ratingDetailsFileUrl={(item as any).ratingDetailsFileUrl} />
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            margin: '10px 0',
          }}
        >
          <UserAccount
            boxProps={{
              sx: {
                display: 'flex',
                alignItems: 'center',
                marginTop: '18px',
              },
            }}
            avatarProps={{
              sx: (theme) => ({
                ...theme.icons.sm,
                fontSize: `${adjustFontSize(12, theme.palette.fontSize)}px`,
                fontWeight: 400,
                backgroundColor: theme.palette.common.gray,
                marginRight: '5px',
              }),
              children: getNameInitials(`${user?.firstName} ${user?.lastName}`),
            }}
          >
            <Typography variant='caption'>
              {user.firstName} {user.lastName}
            </Typography>
          </UserAccount>
        </Box>
      </Box>
      {!isLast && (
        <Divider
          sx={{
            width: '90%',
            margin: 'auto',
          }}
        />
      )}
    </Box>
  );
};
